import { ArrowLeft, Shield, FileText, CheckCircle, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
const BusinessVerificationPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const lastUpdated = "January 31, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Business Verification Policy</h1>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mb-10 p-6 bg-purple-500/10 border border-purple-500/20 rounded-xl">
          <p className="text-purple-300">
            <strong>IMPORTANT:</strong> By submitting a registration request, you authorize MCG to conduct the 
            verification checks described in this policy to ensure platform integrity and user safety.
          </p>
        </div>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">1. Purpose and Scope</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              Metro City Groove (Pty) Ltd ("MCG") is committed to maintaining a trusted and secure platform 
              for all users. This Business Verification Policy outlines the verification procedures we conduct 
              on agency partners to ensure the integrity of our platform and protect all stakeholders.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">2. Verification Checks Conducted</h2>
          </div>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">2.1 CIPC Registration Verification</h3>
              <p className="text-gray-400 mb-2">We verify your business registration with the Companies and Intellectual Property Commission (CIPC), including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Company registration number validity</li>
                <li>Company status (active, deregistered, in liquidation, etc.)</li>
                <li>Registered company name matches application</li>
                <li>Director information</li>
                <li>Registered business address</li>
                <li>Date of incorporation</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">2.2 Tax Compliance Verification</h3>
              <p className="text-gray-400 mb-2">We may verify your tax compliance status, including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Valid tax registration with SARS</li>
                <li>Tax compliance status (where publicly available)</li>
                <li>VAT registration (if applicable)</li>
                <li>PAYE registration (if applicable)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">2.3 Reference Verification</h3>
              <p className="text-gray-400 mb-2">We may contact references to verify:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Business operating history</li>
                <li>Industry reputation and standing</li>
                <li>Previous partnership or client relationships</li>
                <li>Professional conduct and reliability</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">2.4 Identity Verification</h3>
              <p className="text-gray-400 mb-2">We verify the identity of authorized representatives through:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Government-issued identification documents</li>
                <li>Proof of authority to represent the business</li>
                <li>Contact information verification</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">2.5 Online Presence Verification</h3>
              <p className="text-gray-400 mb-2">We may review your online presence, including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Company website and domain ownership</li>
                <li>Social media profiles and activity</li>
                <li>Online reviews and reputation</li>
                <li>Press coverage and media mentions</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">3. Verification Process</h2>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.1 Timeline</h3>
              <p className="text-gray-400">
                The verification process typically takes 24-48 hours for standard applications. Complex cases 
                may require up to 5 business days. We will notify you if additional time or information is needed.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.2 Additional Documentation</h3>
              <p className="text-gray-400 mb-2">We may request additional documentation, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Certified copies of registration documents</li>
                <li>Proof of physical business address</li>
                <li>Bank account confirmation letter</li>
                <li>Professional licenses or certifications</li>
                <li>Insurance certificates</li>
                <li>Letters of good standing</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.3 Verification Outcome</h3>
              <p className="text-gray-400 mb-2">Following verification, your application will be:</p>
              <div className="space-y-3 mt-3">
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-300"><strong>Approved:</strong> You will receive access credentials and onboarding information</p>
                </div>
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-yellow-300"><strong>Pending:</strong> Additional information or documentation is required</p>
                </div>
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-300"><strong>Declined:</strong> Your application did not meet our verification requirements</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">4. Data Handling</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              All information collected during the verification process is handled in accordance with our{" "}
              <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>{" "}
              and the Protection of Personal Information Act (POPIA).
            </p>
            <p>We commit to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Only collecting information necessary for verification purposes</li>
              <li>Storing verification data securely with appropriate access controls</li>
              <li>Not sharing verification data with unauthorized third parties</li>
              <li>Retaining verification records only as long as legally required</li>
              <li>Allowing you to access and correct your verification information</li>
            </ul>
          </div>
        </section>

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">5. Third-Party Verification Services</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              MCG may use accredited third-party verification services to conduct background checks. 
              These services are bound by confidentiality agreements and must comply with South African 
              data protection laws.
            </p>
            <p>Third-party services we may use include:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>CIPC direct verification services</li>
              <li>Credit bureaus for business credit checks</li>
              <li>Professional background check providers</li>
              <li>Industry association membership verification</li>
            </ul>
          </div>
        </section>

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">6. Ongoing Verification</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              Verification is not a one-time process. MCG reserves the right to conduct periodic 
              re-verification to ensure continued compliance. This may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Annual registration status checks</li>
              <li>Tax compliance verification</li>
              <li>Updated documentation requests</li>
              <li>Monitoring for adverse events or changes in business status</li>
            </ul>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <h2 className="text-2xl font-semibold">7. Consequences of Verification Failure</h2>
          </div>
          <div className="space-y-4 text-gray-300 p-6 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
            <p>If verification reveals discrepancies or concerns, MCG may:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Request clarification or additional documentation</li>
              <li>Decline the application</li>
              <li>Suspend existing platform access pending investigation</li>
              <li>Terminate the partnership agreement</li>
              <li>Report suspected fraud or illegal activity to relevant authorities</li>
            </ul>
          </div>
        </section>

        {/* Section 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">8. Appeals Process</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              If your application is declined or your access is suspended, you may appeal the decision 
              within 14 days by:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-gray-400">
              <li>Submitting a written appeal to verification@metrocitygroove.com</li>
              <li>Providing any additional documentation or clarification</li>
              <li>Requesting a review meeting with the Platform Control team</li>
            </ol>
            <p className="text-gray-400 mt-4">
              Appeals will be reviewed within 5 business days, and you will be notified of the outcome in writing.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">9. Your Rights</h2>
          <div className="space-y-4 text-gray-300">
            <p>Under POPIA, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Know what information we hold about you</li>
              <li>Request access to your verification records</li>
              <li>Correct inaccurate information</li>
              <li>Object to processing in certain circumstances</li>
              <li>Lodge a complaint with the Information Regulator</li>
            </ul>
          </div>
        </section>

        {/* Section 10 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">10. Contact Information</h2>
          <div className="space-y-4 text-gray-300">
            <p>For questions regarding this Verification Policy or to exercise your rights, contact us at:</p>
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-white font-semibold">Metro City Groove (Pty) Ltd - Verification Team</p>
              <p className="text-gray-400">Email: verification@metrocitygroove.com</p>
              <p className="text-gray-400">Phone: +27 11 000 0000</p>
              <p className="text-gray-400">Address: Johannesburg, South Africa</p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-16 p-6 bg-white/5 rounded-xl border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Related Documents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/terms" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Privacy Policy
            </Link>
            <Link to="/agency-partnership-agreement" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Agency Partnership Agreement
            </Link>
            <Link to="/gdpr" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              GDPR Compliance
            </Link>
          </div>
        </section>
      </main>
    </div>;
};
export default BusinessVerificationPolicy;